"use client"

import { useMemo, useState, useEffect } from "react"
import { useTheme } from "next-themes"
import { motion, AnimatePresence } from "framer-motion"
import { Phone, MessageSquare, AlertTriangle, Bot, User, MapPin, Pencil, Calendar, Clock, Flame, Zap, Snowflake, Ban, EyeOff, Baby, CheckCircle2 } from "@/lib/icons"
import { toast } from "sonner"
import { LeadLevelAvatar, LEVEL_META, normalizeLevel } from "./lead-level-avatar"
import { LeadSourceBadge } from "./lead-source-badge"
import { ConfirmModal } from "@/components/ui/confirm-modal"
import { supabase, updateLead, confirmInscripcion } from "@/lib/supabase"
import { useWhatsAppProfilePic } from "@/hooks/useWhatsAppProfilePic"
import { getNivelLabel } from "@/lib/educational-levels"
import { getDaysSinceInteraction, getDaysToAutoArchive } from "@/lib/dates"

// Estilos para badge de calidad del lead
const CALIDAD_META = {
  caliente: {
    label: "Caliente",
    icon: Flame,
    color: "bg-red-500/20 text-red-600 dark:bg-red-900/40 dark:text-red-300"
  },
  tibio: {
    label: "Tibio",
    icon: Zap,
    color: "bg-amber-500/20 text-amber-600 dark:bg-amber-900/40 dark:text-amber-300"
  },
  frio: {
    label: "Frío",
    icon: Snowflake,
    color: "bg-blue-500/20 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300"
  },
  spam: {
    label: "Spam",
    icon: Ban,
    color: "bg-slate-500/20 text-slate-500 dark:bg-slate-700/50 dark:text-slate-400"
  },
}

const parseTags = (raw) => {
  if (!raw) return []

  if (Array.isArray(raw)) {
    return raw.map((tag) => String(tag).trim()).filter(Boolean)
  }

  const value = String(raw).trim()
  if (!value) return []

  if (value.startsWith("[") && value.endsWith("]")) {
    try {
      const parsed = JSON.parse(value)
      if (Array.isArray(parsed)) {
        return parsed.map((tag) => String(tag).trim()).filter(Boolean)
      }
    } catch {
      // ignore JSON parse errors and fallback to comma split
    }
  }

  const list = value
    .split(/[,\|]/)
    .map((tag) => tag.trim())
    .filter(Boolean)

  return Array.from(new Set(list))
}

const getTagStyle = (tag) => {
  const normalized = tag.toLowerCase()

  if (normalized === "examen") {
    return {
      className:
        "border border-emerald-300/70 bg-emerald-50 text-emerald-700 dark:border-emerald-500/50 dark:bg-emerald-900/20 dark:text-emerald-200",
      label: "Examen",
    }
  }

  return {
    className:
      "border border-slate-200/80 bg-slate-50 text-slate-600 dark:border-slate-600/60 dark:bg-slate-900/30 dark:text-slate-200",
    label: tag.length ? `${tag.charAt(0).toUpperCase()}${tag.slice(1)}` : tag,
  }
}

// Tiempo relativo legible
const getRelativeTime = (dateStr, nowMs = Date.now()) => {
  if (!dateStr) return null
  const ms = new Date(dateStr).getTime()
  if (Number.isNaN(ms)) return null
  const diffSec = Math.max(0, Math.floor((nowMs - ms) / 1000))
  if (diffSec < 10) return "ahora"
  if (diffSec < 60) return `hace ${diffSec}s`
  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return `hace ${diffMin}m`
  const diffH = Math.floor(diffMin / 60)
  if (diffH < 24) return `hace ${diffH}h`
  const diffD = Math.floor(diffH / 24)
  if (diffD === 1) return "ayer"
  if (diffD < 7) return `hace ${diffD}d`
  return `hace ${Math.floor(diffD / 7)}sem`
}

// Ventana LIVE: el lead se considera "vivo" si tuvo actividad en los últimos 5 min.
const LIVE_WINDOW_MS = 5 * 60 * 1000
const getLiveInfo = (dateStr, nowMs = Date.now()) => {
  if (!dateStr) return null
  const ms = new Date(dateStr).getTime()
  if (Number.isNaN(ms)) return null
  const diffMs = nowMs - ms
  if (diffMs < 0 || diffMs > LIVE_WINDOW_MS) return null
  return { label: getRelativeTime(dateStr, nowMs) }
}

// Formato corto de cita
const formatCitaShort = (dateStr) => {
  if (!dateStr) return null
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return null
  const now = new Date()
  const isToday = d.toDateString() === now.toDateString()
  const tomorrow = new Date(now)
  tomorrow.setDate(tomorrow.getDate() + 1)
  const isTomorrow = d.toDateString() === tomorrow.toDateString()
  const time = d.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })
  if (isToday) return { label: `Hoy ${time}`, urgent: true }
  if (isTomorrow) return { label: `Mañana ${time}`, urgent: false }
  const day = d.toLocaleDateString("es-MX", { day: "numeric", month: "short" })
  return { label: `${day} ${time}`, urgent: false }
}

/* --- Avatar con foto de WhatsApp o fallback a iniciales --- */
// Las URLs de pps.whatsapp.net expiran (403): si la imagen falla, caemos al avatar de iniciales.
function WaAvatar({ picUrl, nivel, size, initials }) {
  const [imgError, setImgError] = useState(false)
  useEffect(() => { setImgError(false) }, [picUrl])
  if (picUrl && !imgError) {
    return (
      <img
        src={picUrl}
        alt=""
        className="rounded-lg object-cover shadow-sm ring-1 ring-slate-200/50 dark:ring-slate-700/50"
        style={{ width: size, height: size }}
        onError={() => setImgError(true)}
      />
    )
  }
  return <LeadLevelAvatar nivel={nivel} size={size} initials={initials} />
}

/* --- NUEVO: pequeño icono WhatsApp inline --- */
function WhatsAppIcon(props) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="20" height="20" viewBox="0 0 48 48">
      <path fill="#fff" d="M4.868,43.303l2.694-9.835C5.9,30.59,5.026,27.324,5.027,23.979C5.032,13.514,13.548,5,24.014,5c5.079,0.002,9.845,1.979,13.43,5.566c3.584,3.588,5.558,8.356,5.556,13.428c-0.004,10.465-8.522,18.98-18.986,18.98c-0.001,0,0,0,0,0h-0.008c-3.177-0.001-6.3-0.798-9.073-2.311L4.868,43.303z"></path><path fill="#fff" d="M4.868,43.803c-0.132,0-0.26-0.052-0.355-0.148c-0.125-0.127-0.174-0.312-0.127-0.483l2.639-9.636c-1.636-2.906-2.499-6.206-2.497-9.556C4.532,13.238,13.273,4.5,24.014,4.5c5.21,0.002,10.105,2.031,13.784,5.713c3.679,3.683,5.704,8.577,5.702,13.781c-0.004,10.741-8.746,19.48-19.486,19.48c-3.189-0.001-6.344-0.788-9.144-2.277l-9.875,2.589C4.953,43.798,4.911,43.803,4.868,43.803z"></path><path fill="#cfd8dc" d="M24.014,5c5.079,0.002,9.845,1.979,13.43,5.566c3.584,3.588,5.558,8.356,5.556,13.428c-0.004,10.465-8.522,18.98-18.986,18.98h-0.008c-3.177-0.001-6.3-0.798-9.073-2.311L4.868,43.303l2.694-9.835C5.9,30.59,5.026,27.324,5.027,23.979C5.032,13.514,13.548,5,24.014,5 M24.014,42.974C24.014,42.974,24.014,42.974,24.014,42.974C24.014,42.974,24.014,42.974,24.014,42.974 M24.014,42.974C24.014,42.974,24.014,42.974,24.014,42.974C24.014,42.974,24.014,42.974,24.014,42.974 M24.014,4C24.014,4,24.014,4,24.014,4C12.998,4,4.032,12.962,4.027,23.979c-0.001,3.367,0.849,6.685,2.461,9.622l-2.585,9.439c-0.094,0.345,0.002,0.713,0.254,0.967c0.19,0.192,0.447,0.297,0.711,0.297c0.085,0,0.17-0.011,0.254-0.033l9.687-2.54c2.828,1.468,5.998,2.243,9.197,2.244c11.024,0,19.99-8.963,19.995-19.98c0.002-5.339-2.075-10.359-5.848-14.135C34.378,6.083,29.357,4.002,24.014,4L24.014,4z"></path><path fill="#40c351" d="M35.176,12.832c-2.98-2.982-6.941-4.625-11.157-4.626c-8.704,0-15.783,7.076-15.787,15.774c-0.001,2.981,0.833,5.883,2.413,8.396l0.376,0.597l-1.595,5.821l5.973-1.566l0.577,0.342c2.422,1.438,5.2,2.198,8.032,2.199h0.006c8.698,0,15.777-7.077,15.78-15.776C39.795,19.778,38.156,15.814,35.176,12.832z"></path><path fill="#fff" fillRule="evenodd" d="M19.268,16.045c-0.355-0.79-0.729-0.806-1.068-0.82c-0.277-0.012-0.593-0.011-0.909-0.011c-0.316,0-0.83,0.119-1.265,0.594c-0.435,0.475-1.661,1.622-1.661,3.956c0,2.334,1.7,4.59,1.937,4.906c0.237,0.316,3.282,5.259,8.104,7.161c4.007,1.58,4.823,1.266,5.693,1.187c0.87-0.079,2.807-1.147,3.202-2.255c0.395-1.108,0.395-2.057,0.277-2.255c-0.119-0.198-0.435-0.316-0.909-0.554s-2.807-1.385-3.242-1.543c-0.435-0.158-0.751-0.237-1.068,0.238c-0.316,0.474-1.225,1.543-1.502,1.859c-0.277,0.317-0.554,0.357-1.028,0.119c-0.474-0.238-2.002-0.738-3.815-2.354c-1.41-1.257-2.362-2.81-2.639-3.285c-0.277-0.474-0.03-0.731,0.208-0.968c0.213-0.213,0.474-0.554,0.712-0.831c0.237-0.277,0.316-0.475,0.474-0.791c0.158-0.317,0.079-0.594-0.04-0.831C20.612,19.329,19.69,16.983,19.268,16.045z" clipRule="evenodd"></path>
    </svg>
  )
}

export function LeadCard({
  lead,
  onViewDetails,
  onOpenConversation,
  onDragStart,
  onDragEnd,
  isDragging,
  accent: accentProp = "#6366f1",
  isHighlighted = false,
  isCompact = false,
  onHide,
}) {
  const levelKey = normalizeLevel(lead.nivel_educativo)
  const levelMeta = LEVEL_META[levelKey]
  const accent = levelMeta?.color ?? accentProp
  const leadLevel = levelMeta?.label || getNivelLabel(lead.nivel_educativo) || lead.nivel_educativo || "Sin nivel"
  const LevelIcon = levelMeta?.icon
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === "dark"
  const tags = useMemo(() => parseTags(lead.tags), [lead.tags])

  // WhatsApp profile picture (uses DB cache, fetches from API only once)
  const { picUrl: waProfilePic } = useWhatsAppProfilePic(lead)

  // Normalizar flag de intervención humana (acepta boolean, "true"/"false", 1/0)
  const isHuman = (() => {
    const v = lead?.intervencion_humana
    if (v === true || v === 1 || v === "1") return true
    if (v === false || v === 0 || v === "0") return false
    if (typeof v === "string") {
      return v.toLowerCase() === "true"
    }
    return Boolean(v)
  })()

  // 🆕 Estado para contador de mensajes sin leer
  const [unreadCount, setUnreadCount] = useState(0)
  const [hasNewMessages, setHasNewMessages] = useState(false)
  const [editingName, setEditingName] = useState(false)
  const [localName, setLocalName] = useState(lead.nombre_tutor || "")

  useEffect(() => {
    if (!editingName) setLocalName(lead.nombre_tutor || "")
  }, [lead.nombre_tutor, editingName])

  // Unread messages — uses global channel event instead of individual subscriptions
  useEffect(() => {
    if (!lead?.id) return
    let mounted = true

    const checkUnreadMessages = async () => {
      try {
        const lastSeenKey = `last_seen_${lead.id}`
        const lastSeenStr = localStorage.getItem(lastSeenKey)
        const lastSeenDate = lastSeenStr ? new Date(lastSeenStr) : new Date(0)

        // Regla: "no leído" = mensaje del lead (in) cuyo created_at sea
        //   1) posterior al último mensaje saliente (asesor/bot), Y
        //   2) posterior al último visto en este dispositivo
        const { data: lastOut } = await supabase
          .from('messages')
          .select('created_at')
          .eq('lead_id', lead.id)
          .eq('direction', 'out')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle()

        if (!mounted) return

        const lastOutDate = lastOut?.created_at ? new Date(lastOut.created_at) : new Date(0)
        const cutoff = lastOutDate > lastSeenDate ? lastOutDate : lastSeenDate

        const { count } = await supabase
          .from('messages')
          .select('*', { count: 'exact', head: true })
          .eq('lead_id', lead.id)
          .eq('direction', 'in')
          .gt('created_at', cutoff.toISOString())

        if (!mounted) return
        setUnreadCount(count || 0)
        setHasNewMessages((count || 0) > 0)
      } catch { /* ignore */ }
    }

    // Initial check
    checkUnreadMessages()

    // Listen to global channel event (1 single WebSocket for all leads)
    const handleGlobalMsg = (event) => {
      const { leadId, direction } = event.detail || {}
      if (leadId === lead.id && direction === "in") {
        checkUnreadMessages()
      }
    }

    const handleConversationSeen = (event) => {
      if (event?.detail?.leadId === lead.id) checkUnreadMessages()
    }

    const handleUnreadSync = (event) => {
      if (String(event?.detail?.leadId) !== String(lead.id)) return
      const nextCount = Number(event?.detail?.count || 0)
      setUnreadCount(nextCount)
      setHasNewMessages(nextCount > 0)
    }

    window.addEventListener("globalNewMessage", handleGlobalMsg)
    window.addEventListener("leadConversationSeen", handleConversationSeen)
    window.addEventListener("leadUnreadSync", handleUnreadSync)

    return () => {
      mounted = false
      window.removeEventListener("globalNewMessage", handleGlobalMsg)
      window.removeEventListener("leadConversationSeen", handleConversationSeen)
      window.removeEventListener("leadUnreadSync", handleUnreadSync)
    }
  }, [lead?.id])

  const initials = useMemo(() => {
    if (!lead.nombre_tutor) return "?"
    const parts = lead.nombre_tutor.trim().split(" ")
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase()
  }, [lead.nombre_tutor])

  const handleDragStart = (event) => {
    event.stopPropagation()
    onDragStart(lead)
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", lead.id)

    // 🎯 Drag preview más pequeño y ligero
    const dragPreview = document.createElement('div')
    dragPreview.style.cssText = `
      position: absolute;
      top: -10000px;
      left: -10000px;
      width: 140px;
      padding: 10px;
      background: ${isDark ? 'rgba(15,23,42,0.92)' : 'rgba(255,255,255,0.94)'};
      border: 1px solid ${accent};
      border-radius: 8px;
      box-shadow: 0 3px 8px rgba(0,0,0,0.06);
      backdrop-filter: blur(4px);
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 11px;
    `

    const escHtml = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;")
    dragPreview.innerHTML = `
      <div style="display: flex; align-items: center; gap: 8px;">
        <div style="width: 32px; height: 32px; border-radius: 8px; background: ${accent}20; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${accent}" stroke-width="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
          </svg>
        </div>
        <div style="flex: 1; min-width: 0;">
          <div style="font-weight: 600; font-size: 13px; color: ${isDark ? '#fff' : '#000'}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
            ${escHtml(lead.nombre_tutor || "Sin nombre")}
          </div>
          <div style="font-size: 11px; color: ${isDark ? '#94a3b8' : '#64748b'}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
            ${escHtml(lead.telefono || "Sin teléfono")}
          </div>
        </div>
      </div>
    `

    document.body.appendChild(dragPreview)
    event.dataTransfer.setDragImage(dragPreview, 70, 14)

    // Limpiar el preview después de un momento
    setTimeout(() => {
      if (dragPreview.parentNode) {
        document.body.removeChild(dragPreview)
      }
    }, 0)
  }

  const handleCall = (event) => {
    if (!lead.telefono) {
      event.preventDefault()
    }
  }

  // Calcular días sin actividad (usando ultima_interaccion o updated_at)
  // 🆕 Memoizado para reaccionar correctamente a cambios en updated_at
  const daysSinceInteraction = useMemo(
    () => getDaysSinceInteraction(lead),
    [lead.ultima_interaccion, lead.updated_at, lead.created_at]
  )

  // Pre-aviso de auto-archivado: días que le quedan antes de archivarse solo
  const daysToArchive = useMemo(
    () => getDaysToAutoArchive(lead),
    [lead.ultima_interaccion, lead.estado_lead, lead.fecha_cita, lead.intervencion_humana, lead.calidad_lead]
  )

  // ─── Indicador LIVE: actividad en los últimos 5 minutos ───
  // nowTick fuerza re-render para que "hace 12s" se actualice solo.
  const [nowTick, setNowTick] = useState(() => Date.now())
  const lastActivityAt = lead.ultima_interaccion || lead.updated_at || null

  const liveInfo = useMemo(
    () => getLiveInfo(lastActivityAt, nowTick),
    [lastActivityAt, nowTick],
  )
  const isLive = liveInfo != null

  // Timer solo cuando el lead está LIVE — refresca cada 15s.
  // Cuando deja de estar live, el effect se desmonta y no consume CPU.
  useEffect(() => {
    if (!isLive) return
    const id = setInterval(() => setNowTick(Date.now()), 15_000)
    return () => clearInterval(id)
  }, [isLive])

  // Refresco inmediato cuando llega un mensaje de este lead.
  useEffect(() => {
    if (!lead?.id) return
    const handler = (e) => {
      if (e?.detail?.leadId === lead.id) setNowTick(Date.now())
    }
    window.addEventListener("globalNewMessage", handler)
    return () => window.removeEventListener("globalNewMessage", handler)
  }, [lead?.id])

  // ─── Badge NEW: muestra solo si lead < 24h Y nadie lo ha abierto aún ───
  // Forzamos rerender cuando cambia el flag de "abierto" via state local
  const [openedAt, setOpenedAt] = useState(() => {
    try {
      return typeof window !== "undefined"
        ? localStorage.getItem(`lead_opened_${lead.id}`)
        : null
    } catch {
      return null
    }
  })

  // Escuchar evento global cuando OTRA tarjeta marca un lead como abierto
  useEffect(() => {
    const handler = (e) => {
      if (e?.detail?.leadId === lead.id) {
        setOpenedAt(e.detail.openedAt || new Date().toISOString())
      }
    }
    window.addEventListener("leadOpened", handler)
    return () => window.removeEventListener("leadOpened", handler)
  }, [lead.id])

  const isNewLead = useMemo(() => {
    if (!lead.created_at) return false
    if (openedAt) return false  // alguien ya lo abrió → ya no es NEW
    const created = new Date(lead.created_at)
    const ageMs = Date.now() - created.getTime()
    return ageMs < 24 * 60 * 60 * 1000
  }, [lead.created_at, openedAt])

  // Inscrito sin confirmar — muestra banner de "Confirmar inscripción"
  const isInscritoPendiente =
    String(lead?.estado_lead || "").toLowerCase() === "inscrito" &&
    !lead?.inscripcion_confirmada_at

  const [confirmingInsc, setConfirmingInsc] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  const openConfirmModal = (e) => {
    e.stopPropagation()
    if (confirmingInsc) return
    setShowConfirmModal(true)
  }

  const handleConfirmInscripcion = async () => {
    if (confirmingInsc) return
    setConfirmingInsc(true)

    // Optimistic UI: marcamos el lead como confirmado AL INSTANTE para que
    // la card desaparezca sin esperar a la BD. Si el UPDATE falla en BD,
    // disparamos un evento de "revertir" para que vuelva a aparecer.
    const optimisticTs = new Date().toISOString()
    window.dispatchEvent(new CustomEvent("leadConfirmed", {
      detail: { leadId: lead.id, data: { inscripcion_confirmada_at: optimisticTs } },
    }))

    const { error } = await confirmInscripcion(lead.id)
    setConfirmingInsc(false)

    if (error) {
      // Revertir el cambio optimista para que la card vuelva.
      window.dispatchEvent(new CustomEvent("leadConfirmReverted", {
        detail: { leadId: lead.id },
      }))
      toast.error(error.message || "No se pudo confirmar la inscripción")
      console.error("[confirmInscripcion] fallo:", error)
      return
    }

    toast.success("Inscripción confirmada · movida al historial")
  }

  // Thresholds alineados con sistema de recordatorios (3, 5, 14 días)
  const isNormal = daysSinceInteraction < 3
  const isWarning = daysSinceInteraction >= 3 && daysSinceInteraction < 5   // 3-4 días (1er recordatorio)
  const isDanger = daysSinceInteraction >= 5 && daysSinceInteraction < 14   // 5-13 días (archivado)
  const isCritical = daysSinceInteraction >= 14                             // 14+ días (3er recordatorio)

  // Cuántos recordatorios se han enviado
  const remindersCount = useMemo(() => {
    let count = 0
    if (lead.recordatorio_1_at) count++
    if (lead.recordatorio_2_at) count++
    if (lead.recordatorio_3_at) count++
    return count
  }, [lead.recordatorio_1_at, lead.recordatorio_2_at, lead.recordatorio_3_at])

  // Visual ring based on inactivity — subtle indicator instead of heavy rings
  const inactivityRingClass = useMemo(() => {
    if (isCritical) return "ring-1 ring-red-300/60 dark:ring-red-700/50"
    if (isDanger) return "ring-1 ring-orange-200/60 dark:ring-orange-700/40"
    if (isWarning) return ""
    return ""
  }, [isCritical, isDanger, isWarning])

  // Border color — keep neutral, let the badge communicate urgency
  const cardBorderColor = useMemo(() => {
    return isDark ? `${accent}30` : `${accent}20`
  }, [isDark, accent])

  const handleOpenChat = (e) => {
    e.stopPropagation()

    // Marcar como visto al abrir el chat
    const lastSeenKey = `last_seen_${lead.id}`
    localStorage.setItem(lastSeenKey, new Date().toISOString())
    setUnreadCount(0)
    setHasNewMessages(false)

    // Marcar el lead como "abierto" para quitar el badge NEW
    if (!openedAt) {
      const now = new Date().toISOString()
      try {
        localStorage.setItem(`lead_opened_${lead.id}`, now)
      } catch { /* ignore */ }
      setOpenedAt(now)
      // Notificar a otras tarjetas en la misma página (si existieran duplicadas)
      window.dispatchEvent(new CustomEvent("leadOpened", { detail: { leadId: lead.id, openedAt: now } }))
    }

    onOpenConversation?.(lead)
  }

  // Definir clase según el modo de visualización - ICONOS MÁS PEQUEÑOS
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 15, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.92, transition: { duration: 0.2 } }}
      whileHover={{
        y: -5,
        scale: 1.015,
        boxShadow: resolvedTheme === "dark"
          ? "0 20px 25px -5px rgba(0, 0, 0, 0.6), 0 10px 10px -5px rgba(0, 0, 0, 0.4)"
          : "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
      }}
      whileTap={{ scale: 0.985 }}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={onDragEnd}
      onClick={(e) => {
        if (e.target.closest('button') || e.target.closest('input')) return
        // Marcar como abierto para quitar el badge NEW
        if (!openedAt) {
          const now = new Date().toISOString()
          try { localStorage.setItem(`lead_opened_${lead.id}`, now) } catch { /* ignore */ }
          setOpenedAt(now)
          window.dispatchEvent(new CustomEvent("leadOpened", { detail: { leadId: lead.id, openedAt: now } }))
        }
        onViewDetails?.(lead)
      }}
      className={`group relative overflow-hidden rounded-lg border shadow-sm transition-all duration-200 cursor-grab active:cursor-grabbing w-full
        ${isDragging ? "opacity-30 scale-105 rotate-1 z-50 shadow-2xl" : ""}
        ${inactivityRingClass} h-full flex flex-col`}
      style={{
        borderColor: cardBorderColor,
        background: resolvedTheme === "dark"
          ? `linear-gradient(135deg, rgba(15,23,42,0.96), color-mix(in oklab, ${accent} 18%, #000))`
          : `linear-gradient(135deg, ${accent}08, #ffffff)`,
      }}
    >
      {/* Badges flotantes (top-right) */}
      <div className="absolute top-1 right-1 z-20 flex flex-col items-end gap-1">
        {isNewLead && (
          <div
            className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[9px] font-black shadow-md ring-2 ring-white dark:ring-slate-900 tracking-wider"
            title="Prospecto nuevo (últimas 24h)"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
            NEW
          </div>
        )}
        {lead._pendingTransfer && (
          <div
            className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-white text-[9px] font-bold shadow-md ring-2 ring-white dark:ring-slate-900"
            style={{ background: "linear-gradient(135deg, #7a1b33, #a3204b)" }}
            title="Tiene una transferencia pendiente"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
            Transfer
          </div>
        )}
      </div>

      {/* Botón ocultar eliminado intencionalmente: ocultaba leads sin forma de recuperarlos */}

      {isCompact ? (
        // ---- VISTA ULTRA COMPACTA ----
        <div className="p-1 px-1.5 flex items-start gap-1.5 min-h-[36px]">
          {/* Avatar ultra pequeño */}
          <div className="relative flex-shrink-0">
            <WaAvatar picUrl={waProfilePic} nivel={lead.nivel_educativo} size={20} initials={initials} />

            {/* Indicador de mensajes no leídos */}
            {hasNewMessages && (
              <span className="absolute -top-0.5 -right-0.5 flex h-3 w-3 items-center justify-center rounded-full bg-red-500 text-[6px] font-bold text-white animate-pulse shadow-md ring-1 ring-white dark:ring-slate-900 z-10">
                {unreadCount > 9 ? '9' : unreadCount}
              </span>
            )}
          </div>

          {/* Contenido principal */}
          <div className="flex-1 min-w-0 flex flex-col gap-2">
            {/* Nombre editable + Modo + Calidad */}
            <div className="flex items-start gap-1.5">
              {editingName ? (
                <input
                  type="text"
                  value={localName}
                  onChange={(e) => setLocalName(e.target.value)}
                  onBlur={async () => {
                    const trimmed = localName.trim()
                    if (trimmed && trimmed !== (lead.nombre_tutor || "")) {
                      await updateLead(lead.id, { nombre_tutor: trimmed })
                    }
                    setEditingName(false)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.target.blur()
                    if (e.key === "Escape") {
                      setLocalName(lead.nombre_tutor || "")
                      setEditingName(false)
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="flex-1 min-w-0 text-[11px] font-medium bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded px-1.5 py-0.5 text-foreground"
                  placeholder="Nombre o apodo"
                  autoFocus
                />
              ) : (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setLocalName(lead.nombre_tutor || ""); setEditingName(true) }}
                  className="flex-1 min-w-0 text-left group/name"
                >
                  <h3 className="text-[11px] font-medium text-foreground leading-tight line-clamp-1 min-w-0 group-hover/name:text-primary flex items-center gap-1">
                    {lead.nombre_tutor || "Sin nombre"}
                    <Pencil className="h-2.5 w-2.5 opacity-60 group-hover/name:opacity-100 flex-shrink-0" />
                  </h3>
                </button>
              )}

              {/* Calidad del lead */}
              {lead.calidad_lead && CALIDAD_META[lead.calidad_lead] && (() => {
                const CalidadIcon = CALIDAD_META[lead.calidad_lead].icon
                return (
                  <span className={`flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-semibold ${CALIDAD_META[lead.calidad_lead].color}`}>
                    <CalidadIcon className="h-2.5 w-2.5" />
                    {CALIDAD_META[lead.calidad_lead].label}
                  </span>
                )
              })()}

              {/* Modo humano/bot (normalizado) */}
              <div className={`flex-shrink-0 flex items-center justify-center h-5.5 w-5.5 rounded-md shadow-sm ${isHuman
                ? "bg-gradient-to-br from-emerald-500 to-teal-600"
                : "bg-gradient-to-br from-rose-600 to-rose-800"
                }`} title={isHuman ? "Modo Humano" : "Bot Activo"}>
                {isHuman ? <User className="h-3 w-3 text-white" /> : <Bot className="h-3 w-3 text-white" />}
              </div>

              {/* Badge de fuente del lead (vista compacta) */}
              {lead.source && (
                <LeadSourceBadge
                  source={lead.source}
                  sourceDetail={lead.source_detail}
                  size={20}
                />
              )}
            </div>

            {/* Teléfono — inline compacto (sin línea dedicada de campus) */}
            <div className="flex items-center gap-1.5 text-[9px] text-muted-foreground">
              <Phone className="h-2.5 w-2.5 flex-shrink-0" style={{ color: accent }} />
              <span className="truncate">{lead.telefono || "Sin tel"}</span>
              {liveInfo && (
                <span
                  className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8.5px] font-bold leading-none bg-emerald-500 text-white shadow-sm shadow-emerald-500/30"
                  title={`Conversación viva · ${liveInfo.label}`}
                >
                  <span className="h-1 w-1 rounded-full bg-white animate-pulse" />
                  {liveInfo.label}
                </span>
              )}
            </div>

            {/* Badge de cita (compacta) */}
            {lead.fecha_cita && (() => {
              const cita = formatCitaShort(lead.fecha_cita)
              if (!cita) return null
              return (
                <div className={`flex items-center gap-1 text-[8px] font-semibold rounded px-1 py-0.5 ${cita.urgent
                  ? "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"
                  : "bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300"
                  }`}>
                  <Calendar className="h-2 w-2" />
                  <span>{cita.label}</span>
                </div>
              )
            })()}

            {/* Nivel + Acciones + Días inactivo */}
            <div className="flex items-center justify-between gap-2 mt-0.5">
              {/* Nivel educativo */}
              <div className="flex items-center gap-1">
                <span
                  className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-semibold"
                  style={{
                    borderColor: isDark ? `${accent}70` : `${accent}40`,
                    background: isDark
                      ? `color-mix(in oklab, ${accent} 30%, rgba(15,23,42,0.9))`
                      : `${accent}15`,
                    color: isDark ? "#f8fafc" : accent,
                  }}
                >
                  {LevelIcon ? <LevelIcon className="h-2.5 w-2.5" /> : null}
                  <span className="leading-none">{leadLevel}</span>
                </span>

                {/* Badge de familia (hijos múltiples) */}
                {(lead.num_hijos ?? 0) >= 2 && (
                  <span
                    className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[9px] font-semibold bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300"
                    title={`Familia con ${lead.num_hijos} hijos`}
                  >
                    <Baby className="h-2.5 w-2.5" />
                    <span className="leading-none">{lead.num_hijos}</span>
                  </span>
                )}

                {/* Badge de días de inactividad + recordatorios */}
                {(isWarning || isDanger || isCritical) && (
                  <span
                    className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-bold leading-none ${isCritical
                      ? "bg-red-500 text-white animate-pulse shadow-sm shadow-red-500/30"
                      : isDanger
                        ? "bg-orange-500 text-white shadow-sm shadow-orange-500/20"
                        : "bg-amber-100 text-amber-800 ring-1 ring-amber-300/50"
                      }`}
                    title={`${daysSinceInteraction}d inactivo · ${remindersCount}/3 recordatorios enviados`}
                  >
                    {daysSinceInteraction}d
                    {remindersCount > 0 && (
                      <span className="ml-0.5 opacity-80">R{remindersCount}</span>
                    )}
                  </span>
                )}

                {/* Pre-aviso de auto-archivado */}
                {daysToArchive !== null && (
                  <span
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-bold leading-none bg-rose-600 text-white shadow-sm shadow-rose-600/30 animate-pulse"
                    title={`Sin respuesta del prospecto: se archivará automáticamente en ${daysToArchive} día(s). Escríbele o agenda una cita para protegerlo.`}
                  >
                    🗂 {daysToArchive === 0 ? "hoy" : `${daysToArchive}d`}
                  </span>
                )}

                {/* Botón WhatsApp */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={handleOpenChat}
                    className="relative p-1 rounded-md border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all shadow-sm active:scale-95"
                    style={{ borderColor: `${accent}20` }}
                    title="Abrir conversación"
                  >
                    <WhatsAppIcon className="h-3.5 w-3.5" style={{ color: accent }} />
                    {hasNewMessages && (
                      <span className="absolute -top-0.5 -right-0.5 flex h-3 w-3 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white animate-pulse shadow-md ring-1 ring-white dark:ring-slate-900 z-10">
                        {`+${Math.min(unreadCount, 9)}`}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Removed old inactivity indicator - now shown inline with level */}
        </div>
      ) : (
        // ---- VISTA NORMAL — compacta, badges arriba ----
        <div className="px-2 py-1.5 flex flex-col gap-1">
          {/* Fila 1: Badges (Nivel + Calidad) | IA/Manual */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1 flex-wrap min-w-0">
              <span
                className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold leading-none"
                style={{
                  background: isDark
                    ? `color-mix(in oklab, ${accent} 30%, rgba(15,23,42,0.9))`
                    : `${accent}15`,
                  color: isDark ? "#f8fafc" : accent,
                }}
              >
                {LevelIcon ? <LevelIcon className="h-2.5 w-2.5" /> : null}
                <span>{leadLevel}</span>
              </span>

              {lead.calidad_lead && CALIDAD_META[lead.calidad_lead] && (() => {
                const CalidadIcon = CALIDAD_META[lead.calidad_lead].icon
                return (
                  <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-bold leading-none ${CALIDAD_META[lead.calidad_lead].color}`}>
                    <CalidadIcon className="h-2.5 w-2.5" />
                    {CALIDAD_META[lead.calidad_lead].label}
                  </span>
                )
              })()}

              {(lead.num_hijos ?? 0) >= 2 && (
                <span
                  className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-bold leading-none bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300"
                  title={`Familia con ${lead.num_hijos} hijos`}
                >
                  <Baby className="h-2.5 w-2.5" />
                  {lead.num_hijos}
                </span>
              )}
            </div>

            <span
              className={`flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-bold border leading-none ${
                isHuman
                  ? "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-300/60 dark:border-emerald-700/50"
                  : "bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 border-indigo-300/60 dark:border-indigo-700/50"
              }`}
              title={isHuman ? "Modo Manual" : "Bot Activo"}
            >
              {isHuman ? <User className="h-2.5 w-2.5" /> : <Bot className="h-2.5 w-2.5" />}
              {isHuman ? "Manual" : "IA"}
            </span>
          </div>

          {/* Fila 2: Avatar pequeño + Nombre + Teléfono */}
          <div className="flex items-center gap-2">
            <div className="relative flex-shrink-0">
              <WaAvatar picUrl={waProfilePic} nivel={lead.nivel_educativo} size={28} initials={initials} />
              {hasNewMessages && (
                <span className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-rose-500 text-[8px] font-bold text-white animate-pulse shadow-md ring-1.5 ring-white dark:ring-slate-900 z-10">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </div>

            <div className="flex-1 min-w-0">
              {editingName ? (
                <input
                  type="text"
                  value={localName}
                  onChange={(e) => setLocalName(e.target.value)}
                  onBlur={async () => {
                    const trimmed = localName.trim()
                    if (trimmed && trimmed !== (lead.nombre_tutor || "")) {
                      await updateLead(lead.id, { nombre_tutor: trimmed })
                    }
                    setEditingName(false)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.target.blur()
                    if (e.key === "Escape") {
                      setLocalName(lead.nombre_tutor || "")
                      setEditingName(false)
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="w-full text-[12px] font-bold bg-white dark:bg-slate-800 border border-rose-300 dark:border-rose-600 rounded-md px-1.5 py-0.5 text-foreground focus:outline-none focus:ring-2 focus:ring-rose-400 shadow-sm"
                  placeholder="Nombre o apodo"
                  autoFocus
                />
              ) : (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setLocalName(lead.nombre_tutor || ""); setEditingName(true) }}
                  className="w-full text-left group/editname"
                >
                  <h3 className="text-[13px] font-bold text-foreground leading-tight line-clamp-1 flex items-center gap-1">
                    <span className="truncate">{lead.nombre_tutor || "Sin nombre"}</span>
                    <Pencil className="h-2.5 w-2.5 opacity-0 group-hover/editname:opacity-70 text-rose-400 transition-opacity flex-shrink-0" />
                  </h3>
                </button>
              )}

              {lead.telefono && (
                <p className="text-[10px] text-muted-foreground tabular-nums truncate leading-tight">
                  {lead.telefono}
                </p>
              )}
            </div>
          </div>

          {/* Fila 3: Procedencia | Tiempo */}
          <div className="flex items-center justify-between gap-2">
            {lead.source ? (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-slate-300/60 dark:border-slate-700/60 bg-slate-50 dark:bg-slate-800/40 leading-none">
                <LeadSourceBadge
                  source={lead.source}
                  sourceDetail={lead.source_detail}
                  expanded
                />
              </span>
            ) : (
              <span />
            )}

            <div className="flex items-center gap-1.5 flex-shrink-0">
              {(isWarning || isDanger || isCritical) && (
                <span
                  className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-bold leading-none ${
                    isCritical
                      ? "bg-red-500 text-white animate-pulse"
                      : isDanger
                        ? "bg-orange-500 text-white"
                        : "bg-amber-100 text-amber-800 ring-1 ring-amber-300/50"
                  }`}
                  title={`${daysSinceInteraction}d inactivo · ${remindersCount}/3 recordatorios`}
                >
                  <AlertTriangle className="h-2.5 w-2.5 shrink-0" />
                  {daysSinceInteraction}d
                </span>
              )}

              {daysToArchive !== null && (
                <span
                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-bold leading-none bg-rose-600 text-white shadow-sm shadow-rose-600/30 animate-pulse"
                  title={`Se archivará automáticamente en ${daysToArchive} día(s)`}
                >
                  🗂 {daysToArchive === 0 ? "hoy" : `${daysToArchive}d`}
                </span>
              )}

              {liveInfo ? (
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500 text-white text-[9px] font-bold leading-none whitespace-nowrap shadow-sm shadow-emerald-500/30"
                  title={`Conversación viva · ${liveInfo.label}`}
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
                  {liveInfo.label}
                </span>
              ) : (lead.ultima_interaccion || lead.updated_at) ? (
                <span className="inline-flex items-center gap-1 text-[9.5px] text-muted-foreground whitespace-nowrap">
                  <span className="h-1.5 w-1.5 rounded-full bg-slate-400 inline-block" />
                  {getRelativeTime(lead.ultima_interaccion || lead.updated_at, nowTick)}
                </span>
              ) : null}
            </div>
          </div>

          {/* Cita programada — compacta */}
          {lead.fecha_cita && (() => {
            const cita = formatCitaShort(lead.fecha_cita)
            if (!cita) return null
            return (
              <div className={`flex items-center gap-1 text-[9.5px] font-semibold rounded px-1.5 py-0.5 leading-none ${
                cita.urgent
                  ? "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"
                  : "bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300"
              }`}>
                <Calendar className="h-2.5 w-2.5 flex-shrink-0" />
                <span>{cita.label}</span>
              </div>
            )
          })()}
        </div>
      )}

      {/* Banner: Confirmar inscripción (solo si lead inscrito sin confirmar) */}
      {isInscritoPendiente && (
        <button
          type="button"
          onClick={openConfirmModal}
          disabled={confirmingInsc}
          className="relative z-10 mt-1 mx-1 mb-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-[10px] font-extrabold uppercase tracking-wider shadow-sm transition-all active:scale-95 disabled:opacity-60"
          title="Mover al historial de inscritos del mes"
        >
          <CheckCircle2 className="h-3 w-3" />
          {confirmingInsc ? "Confirmando..." : "Confirmar inscripción"}
        </button>
      )}

      {/* Modal de confirmación bonito */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmInscripcion}
        type="success"
        title="¿Confirmar la inscripción?"
        message={`${lead.nombre_tutor || "Este prospecto"} se moverá al historial de inscritos del mes y dejará de aparecer en el Kanban. Esta acción quedará registrada con la fecha de hoy.`}
      />
    </motion.article>
  )
}
